import { useState, useMemo, useCallback } from 'react';
import { load, dump } from 'js-yaml';
import './ToolStyles.css';

const JsonYamlConverter = () => {
  const [input, setInput] = useState('');
  const [direction, setDirection] = useState<'json2yaml' | 'yaml2json'>('json2yaml');

  const sourceLabel = direction === 'json2yaml' ? 'JSON' : 'YAML';
  const targetLabel = direction === 'json2yaml' ? 'YAML' : 'JSON';

  // 实时转换
  const { output, error } = useMemo(() => {
    if (!input.trim()) return { output: '', error: null };

    try {
      if (direction === 'json2yaml') {
        const parsed = JSON.parse(input);
        const yamlStr = dump(parsed, {
          indent: 2,
          lineWidth: -1, // 不自动折行
          noRefs: true,
        });
        return { output: yamlStr, error: null };
      } else {
        const parsed = load(input);
        const jsonStr = JSON.stringify(parsed, null, 2);
        return { output: jsonStr, error: null };
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      return { output: '', error: msg };
    }
  }, [input, direction]);

  // 方向切换：交换 input 和 output
  const handleToggleDirection = useCallback(() => {
    setDirection((d) => (d === 'json2yaml' ? 'yaml2json' : 'json2yaml'));
    if (output) {
      setInput(output);
    }
  }, [output]);

  const handleClear = () => {
    setInput('');
  };

  const handleCopy = async () => {
    if (output) {
      await navigator.clipboard.writeText(output);
    }
  };

  return (
    <div className="tool-container">
      {/* 方向指示 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="input-label">{sourceLabel}</span>
        <button
          className="tool-button"
          onClick={handleToggleDirection}
          title="切换转换方向"
          style={{ padding: '6px 14px', fontSize: 16 }}
        >
          ⇄
        </button>
        <span className="input-label">{targetLabel}</span>
      </div>

      {/* 双栏 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        {/* 输入 */}
        <div className="input-group">
          <textarea
            className="tool-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={14}
            placeholder={`输入 ${sourceLabel}...`}
          />
          {error && (
            <div className="error-message" style={{ fontSize: 12 }}>
              ⚠️ {error}
            </div>
          )}
        </div>

        {/* 输出 */}
        <div className="output-group">
          <div className="output-header">
            <span className="output-label">{targetLabel}</span>
            <button className="copy-button" onClick={handleCopy} disabled={!output}>
              复制
            </button>
          </div>
          <pre
            className="tool-output code"
            style={{
              minHeight: 200,
              maxHeight: 420,
              fontSize: 13,
              fontFamily: "'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace",
              margin: 0,
            }}
          >
            {output || (
              <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                转换结果...
              </span>
            )}
          </pre>
        </div>
      </div>

      {/* 操作 */}
      <div className="button-group">
        <button className="tool-button" onClick={handleClear}>
          清空
        </button>
      </div>
    </div>
  );
};

export default JsonYamlConverter;
